import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { userService } from '../../../../api';
import {
  Card,
  Colors,
  Form,
  PasswordInput,
  Text,
  Typography,
} from 'goalsetter-webapp-components';
import {
  Container,
  HeaderCard,
  ImageContainer,
  cardStyle,
  lastInputStyle,
  titleStyle,
} from './styles';
import { INPUT_NAMES } from './inputNames';
import { Image } from '@mantine/core';
import Images from '../../../../assets/img/Images';
import { validationHandler } from './validationHandler';
import { LANDINGS_URL } from '../../../../utils';
import PasswordErrorModal from '../../../components/Modals/PasswordErrorModal';
import { isEmpty } from 'lodash';
import { RoutesGs } from '../../../../router/RoutesList';
import LogoHeader from 'app/components/LogoHeader';

export const ResetPassword = () => {
  const {
    setError,
    clearErrors,
    register,
    formState: { errors },
  } = useForm();
  const [newPass, setNewPass] = useState<string>('');
  const [repeatPass, setRepeatPass] = useState<string>('');
  const allInputsAreEmpty = [newPass, repeatPass].some((str) => Boolean(str));

  const { state } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!state?.hash) {
      const landingsURL = `${LANDINGS_URL}?linkResetPasswordExpired=1`;
      window.location.href = landingsURL;
    }
  }, []);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [descriptionError, setDescriptionError] = useState<string>('');

  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    if (newPass === '' || repeatPass === '') {
      setError('form', { message: 'Empty' });
      return;
    }
    clearErrors();
  }, [newPass, repeatPass, allInputsAreEmpty, clearErrors, setError]);

  const onBlurNewPass = () => {
    clearErrors();
    validateNewPass();
    validateMatch();
  };

  const onBlurRepeatPass = () => {
    clearErrors();
    validateRepeatPass();
    validateMatch();
  };

  const validateNewPass = () =>
    validationHandler(setError, newPass, INPUT_NAMES.NEW_PASS);

  const validateRepeatPass = () =>
    validationHandler(setError, repeatPass, INPUT_NAMES.REPEAT_PASS);

  const validateMatch = () => {
    if (!!newPass && !!repeatPass) {
      validationHandler(setError, repeatPass, INPUT_NAMES.REPEAT_PASS, newPass);
    }
  };

  const handleSubmit = () => {
    validateNewPass();
    validateRepeatPass();
    validateMatch();

    if (!isEmpty(errors) || isLoading) return;

    setIsLoading(true);

    userService
      .changePassword(state.hash, newPass)
      .then(() => {
        navigate(RoutesGs.SuccessPassword);
      })
      .catch((response) => {
        if (
          response.status === null ||
          response.status === undefined ||
          response.status === 500
        ) {
          setDescriptionError(String(t('resetPassword.errorDescription')));
          setIsModalVisible(true);
        }

        if (response.status === 409) {
          setDescriptionError(String(t('resetPassword.errorExpired')));
          setIsModalVisible(true);
        }

        if (response.status === 400) {
          const responseJson = response.response.data;

          if (responseJson['status-description']) {
            setDescriptionError(responseJson['status-description']);

            if (responseJson['status-code'] === 4053) {
              setDescriptionError(String(t('resetPassword.tooEasy')));
            }

            if (
              responseJson['status-code'] === 4921 ||
              responseJson['status-code'] === 4922 ||
              responseJson['status-code'] === 4923
            ) {
              setDescriptionError(String(t('resetPassword.errorPersonalData')));
            }

            setIsModalVisible(true);
          }
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const closeModal = () => setIsModalVisible(false);

  return (
    <Container>
      <LogoHeader />
      <Card style={cardStyle}>
        <HeaderCard>
          <ImageContainer>
            <Image src={Images.PASSWORD_USER} />
          </ImageContainer>
          <Text
            type={Typography.GSH5}
            color={Colors.BLACK}
            style={titleStyle}
          >
            {t('resetPassword.title')}
          </Text>
        </HeaderCard>
        <Form
          onSubmit={handleSubmit}
          errors={errors}
          isLoading={isLoading}
          buttonLabel={String(t('resetPassword.reset'))}
        >
          <PasswordInput
            label={t('resetPassword.newPass')}
            placeholder=""
            onChangeValue={setNewPass}
            onBlur={onBlurNewPass}
            value={newPass}
            error={
              errors[INPUT_NAMES.NEW_PASS]?.message
                ? String(errors[INPUT_NAMES.NEW_PASS]?.message)
                : undefined
            }
            register={register}
            name={INPUT_NAMES.NEW_PASS}
            showValidations={false}
            showIcon
          />
          <PasswordInput
            label={t('resetPassword.repeatPass')}
            placeholder=""
            onChangeValue={setRepeatPass}
            onBlur={onBlurRepeatPass}
            value={repeatPass}
            error={
              errors[INPUT_NAMES.REPEAT_PASS]?.message
                ? String(errors[INPUT_NAMES.REPEAT_PASS]?.message)
                : undefined
            }
            register={register}
            name={INPUT_NAMES.REPEAT_PASS}
            style={lastInputStyle}
            showValidations={false}
            showIcon
          />
        </Form>
      </Card>

      <PasswordErrorModal
        show={isModalVisible}
        description={descriptionError}
        onClose={closeModal}
      />
    </Container>
  );
};
